// Skills v2 — the at-a-glance overview band at the top of the Skills page.
// Turns the module's entry into a landing: how many skills you have, how many
// agents they cover, the average proven lift — plus one-tap quick actions into
// the three main jobs. Defensive: a failed/absent fetch just shows dashes.
import { useEffect, useState } from "react";
import { COLORS } from "../../theme/colors";
import { skillsApi } from "../../api/client";

const BORDER = COLORS.border || "#e2e5eb";
const MUTED = COLORS.textMuted || "#6b7280";
const DIM = COLORS.textDim || "#9090a8";
const GREEN = COLORS.green || "#10b981";

function unwrapArray(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

function Stat({ label, value, color }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 96 }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: DIM }}>{label}</span>
      <span style={{ fontSize: 20, fontWeight: 750, fontVariantNumeric: "tabular-nums", color: color || COLORS.text }}>{value}</span>
    </div>
  );
}

export default function SkillsOverviewStrip({ onGo }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = unwrapArray(await skillsApi.yourSkills());
        if (alive) setRows(data);
      } catch {
        if (alive) setRows([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const skillCount = rows ? rows.length : null;
  const agentsCovered = rows
    ? new Set(rows.flatMap((r) => (Array.isArray(r.agent_ids) ? r.agent_ids : []))).size
    : null;
  const lifts = rows ? rows.map((r) => r.impact_pct).filter((v) => Number.isFinite(v)) : [];
  const avgLift = lifts.length ? Math.round(lifts.reduce((a, b) => a + b, 0) / lifts.length) : null;

  const quick = (label, tab, primary) => (
    <button
      type="button"
      onClick={() => onGo(tab)}
      style={{
        font: "inherit", fontSize: 12.5, fontWeight: primary ? 700 : 600,
        color: primary ? "#fff" : MUTED, background: primary ? COLORS.accent : COLORS.surface,
        border: primary ? "none" : `1px solid ${BORDER}`, borderRadius: 999, padding: "8px 14px",
        cursor: "pointer", whiteSpace: "nowrap", boxShadow: primary ? `0 0 16px ${COLORS.accentGlow}` : "none",
      }}
    >{label}</button>
  );

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap",
      padding: "14px 16px", borderRadius: 14, background: COLORS.surface,
      border: `1px solid ${BORDER}`, marginBottom: 14,
    }}>
      <Stat label="Skills" value={skillCount == null ? "—" : skillCount} />
      <Stat label="Agents covered" value={agentsCovered == null ? "—" : agentsCovered} />
      <Stat label="Avg lift" value={avgLift == null ? "—" : `▲ +${avgLift}%`} color={avgLift != null ? GREEN : COLORS.text} />
      <span style={{ flex: 1 }} />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {quick("＋ Add a skill", "author", true)}
        {quick("Browse gallery", "gallery")}
        {quick("Quality & Improve →", "quality")}
      </div>
    </div>
  );
}
